import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, GraduationCap, Users, Award, BookOpen } from 'lucide-react';
import IrpLeadModal from './IrpLeadModal';


const SLIDES = [
  {
    id: 1,
    type: 'gradient',
    colors: { from: '#0056D2', via: '#003E99', to: '#002B6B', blob: '#4A90D9' },
    image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1600&h=900&fit=crop&auto=format&q=85',
    badge: 'Trained by Professionals',
    headline: null,
    sub: 'Industry-leading training programs in Data Science, ServiceNow, Salesforce, Cloud Computing and more. Launch your tech career with expert-led courses.',
    cta1: { label: 'Explore Programs', href: '#services' },
    cta2: { label: 'Get in Touch', href: '#contact' },
  },
  {
    id: 5,
    type: 'gradient',
    colors: { from: '#4338CA', via: '#312E81', to: '#1E1B4B', blob: '#818CF8' },
    image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1600&h=900&fit=crop&auto=format&q=85',
    badge: 'New Product',
    headline: 'Interview Readiness Platform (IRP)',
    sub: '30 structured mock interview rounds with real industry consultants. Open-book format. Placement guaranteed.',
    cta1: { label: 'Start Your IRP Journey', href: 'https://a7-mock-interview.vercel.app/login' },
    cta2: { label: 'Learn More', href: '#contact' },
    features: ['30 Rounds', 'Human Experts', 'Open Book', '1-on-1 Practice'],
  },
  {
    id: 2,
    type: 'gradient',
    colors: { from: '#6D28D9', via: '#4C1D95', to: '#2E1065', blob: '#A78BFA' },
    image: 'https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=1600&h=900&fit=crop&auto=format&q=85',
    badge: 'Expert-Led Training',
    headline: 'Connect From Anywhere\nOnline',
    sub: 'Our trainers bring 10+ years of hands-on industry experience across Data Science, Cloud, Salesforce, ServiceNow and more — teaching real tools used in real companies.',
    cta1: { label: 'Meet Our Team', href: '#team' },
    cta2: { label: 'Explore Courses', href: '#services' },
    highlights: [
      { icon: BookOpen,  text: '10+ years avg. industry experience per trainer' },
      { icon: Award,     text: 'Certified professionals from top MNCs'          },
      { icon: Users,     text: 'Small batches — maximum individual attention'   },
    ],
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [irpModalOpen, setIrpModalOpen] = useState(false);

  const next = useCallback(() => setCurrent(c => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [paused, next]);

  return (
    <section
      data-testid="hero-section"
      className="relative pt-[104px] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ── Slides ── */}
      <div className="relative" style={{ minHeight: '88vh' }}>
        {SLIDES.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
          >
            {/* Background */}
            {slide.type === 'gradient' ? (
              <>
                {/* Background image */}
                {slide.image && (
                  <div className="absolute inset-0">
                    <img src={slide.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {/* Gradient colour overlay — semi-transparent so image shows through */}
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${slide.colors.from}DD 0%, ${slide.colors.via}CC 50%, ${slide.colors.to}EE 100%)` }} />
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <div className="absolute top-20 right-20 w-72 h-72 bg-white rounded-full blur-3xl" />
                  <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: slide.colors.blob }} />
                </div>
                {/* Wave lines */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[
                    { cls:'wave-anim-1', top:'15%', d:"M0,40 C360,8 720,72 1080,40 C1440,8 1800,72 2160,40 C2520,8 2880,72 2880,40", vb:"0 0 2880 80", sw:"2.5", op:"0.14" },
                    { cls:'wave-anim-2', top:'35%', d:"M0,40 C360,72 720,8 1080,40 C1440,72 1800,8 2160,40 C2520,72 2880,8 2880,40",  vb:"0 0 2880 80", sw:"2",   op:"0.09" },
                    { cls:'wave-anim-3', top:'58%', d:"M0,30 C240,6 480,54 720,30 C960,6 1200,54 1440,30 C1680,6 1920,54 2160,30 C2400,6 2640,54 2880,30",  vb:"0 0 2880 60", sw:"1.8", op:"0.18" },
                    { cls:'wave-anim-4', top:'78%', d:"M0,30 C240,54 480,6 720,30 C960,54 1200,6 1440,30 C1680,54 1920,6 2160,30 C2400,54 2640,6 2880,30",   vb:"0 0 2880 60", sw:"2.2", op:"0.07" },
                  ].map((w, wi) => (
                    <div key={wi} className={`${w.cls} absolute`} style={{ top: w.top, width: '200%', left: 0 }}>
                      <svg viewBox={w.vb} xmlns="http://www.w3.org/2000/svg" className="w-full h-14">
                        <path d={w.d} fill="none" stroke={`rgba(255,255,255,${w.op})`} strokeWidth={w.sw} />
                      </svg>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="absolute inset-0">
                  <img src={slide.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-black/60" />
              </>
            )}

            {/* Slide Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex items-center" style={{ minHeight: 'calc(88vh - 64px)' }}>
              {slide.id === 1 && <SlideMain slide={slide} onTryIrp={() => setIrpModalOpen(true)} />}
              {slide.id === 2 && <SlideFaculty slide={slide} />}
              {slide.id === 5 && <SlideIRP slide={slide} onTryIrp={() => setIrpModalOpen(true)} />}
            </div>
          </div>
        ))}

        {/* ── Prev / Next Arrows ── */}
        <button
          onClick={prev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/35 backdrop-blur-sm flex items-center justify-center transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={next}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/35 backdrop-blur-sm flex items-center justify-center transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>

        {/* ── Dot Navigation ── */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 h-2.5 bg-white' : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/60'}`}
            />
          ))}
        </div>
      </div>

      {irpModalOpen && <IrpLeadModal onClose={() => setIrpModalOpen(false)} />}
    </section>
  );
}

/* ── Slide 1: Main ── */
function SlideMain({ slide, onTryIrp }) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center w-full py-20 lg:py-28">
      <div className="space-y-8">
        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm font-medium">
          <GraduationCap className="w-4 h-4" />
          {slide.badge}
        </div>
        <h1 className="font-heading font-bold text-white leading-tight whitespace-nowrap" style={{ fontSize: 'clamp(1.1rem, 2.6vw, 2rem)' }}>
          <span className="tagline-glow tl-d1">LEARN</span>
          <span className="tagline-word tl-d2 text-white/40 mx-2">•</span>
          <span className="tagline-word tl-d3">TRANSFORM</span>
          <span className="tagline-word tl-d4 text-white/40 mx-2">•</span>
          <span className="tagline-glow tl-d5">GET HIRED</span>
        </h1>
        <p className="font-body text-lg text-white/80 max-w-lg leading-relaxed">{slide.sub}</p>
        <div className="flex flex-wrap gap-4">
          <a href={slide.cta1.href} data-testid="hero-explore-btn" className="inline-flex items-center gap-2 font-body font-semibold text-sm bg-white text-[#0056D2] px-7 py-3.5 rounded-md hover:bg-[#F0F6FF] transition-colors">
            {slide.cta1.label} <ArrowRight className="w-4 h-4" />
          </a>
          <a href={slide.cta2.href} data-testid="hero-contact-btn" className="inline-flex items-center gap-2 font-body font-semibold text-sm bg-transparent text-white px-7 py-3.5 rounded-md border border-white/30 hover:bg-white/10 transition-colors">
            {slide.cta2.label}
          </a>
        </div>
        <div className="grid grid-cols-4 gap-4 pt-6 border-t border-white/15 max-w-md">
          {[
            { value: '6+', label: 'Courses' },
            { value: '18+', label: 'Yrs Expertise' },
            { value: '95%', label: 'Placement' },
            { value: '30', label: 'Mock Rounds' },
          ].map(s => (
            <div key={s.label}>
              <p className="font-heading font-bold text-2xl text-white">{s.value}</p>
              <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block lg:scale-125 lg:origin-center relative">
        <img
          src="/training-to-placement-clean.svg"
          alt="Training to placement journey: Expert Training, Mock Interviews, Get Placed"
          className="w-full"
        />
        <a
          href={slide.cta1.href}
          aria-label="Explore Programs"
          className="absolute"
          style={{ left: '27.78%', top: '87.91%', width: '23.61%', height: '8.84%' }}
        />
        <button
          type="button"
          onClick={onTryIrp}
          aria-label="Try IRP"
          className="absolute"
          style={{ left: '54.17%', top: '87.91%', width: '18.06%', height: '8.84%' }}
        />
      </div>
    </div>
  );
}

/* ── Slide 2: Faculty ── */
function SlideFaculty({ slide }) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center w-full py-20 lg:py-28">
      <div className="space-y-6 text-center lg:text-left">
        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm font-medium">
          <GraduationCap className="w-4 h-4" />
          {slide.badge}
        </div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white leading-tight">
          {slide.headline.split('\n').map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </h1>
        <p className="font-body text-lg text-white/80 leading-relaxed max-w-lg mx-auto lg:mx-0">{slide.sub}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
          <a href={slide.cta1.href} className="inline-flex items-center gap-2 font-semibold text-sm bg-white text-[#0056D2] px-7 py-3.5 rounded-md hover:bg-[#F0F6FF] transition-colors">
            {slide.cta1.label} <ArrowRight className="w-4 h-4" />
          </a>
          <a href={slide.cta2.href} className="inline-flex items-center gap-2 font-semibold text-sm text-white border border-white/30 px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors">
            {slide.cta2.label}
          </a>
        </div>
        <div className="flex flex-col gap-3">
          {slide.highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                <Icon className="w-5 h-5 text-[#A8D4FF] flex-shrink-0" />
                <span className="text-white/90 text-sm font-medium">{h.text}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="hidden lg:block lg:scale-125 lg:origin-left">
        <img
          src="/connect-online-hero-banner.svg"
          alt="Live virtual classroom with trainer and students connecting online"
          className="w-full"
        />
      </div>
    </div>
  );
}

/* ── Slide 5: IRP ── */
function SlideIRP({ slide, onTryIrp }) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center w-full py-20 lg:py-28">
      <div className="space-y-6">
        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm font-medium">
          <BookOpen className="w-4 h-4" />
          {slide.badge}
        </div>
        <h1 className="font-heading font-bold text-white leading-tight text-4xl sm:text-5xl">{slide.headline}</h1>
        <p className="font-body text-lg text-white/80 max-w-lg leading-relaxed">{slide.sub}</p>
        <div className="flex flex-wrap gap-4">
          <button
            type="button"
            onClick={onTryIrp}
            className="inline-flex items-center gap-2 font-semibold text-sm bg-white text-[#0056D2] px-7 py-3.5 rounded-md hover:bg-[#F0F6FF] transition-colors"
          >
            {slide.cta1.label} <ArrowRight className="w-4 h-4" />
          </button>
          <a href={slide.cta2.href} className="inline-flex items-center gap-2 font-semibold text-sm text-white border border-white/30 px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors">
            {slide.cta2.label}
          </a>
        </div>
        <div className="flex flex-wrap gap-3">
          {slide.features.map(f => (
            <span key={f} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium px-5 py-2.5 rounded-full">
              ✓ {f}
            </span>
          ))}
        </div>
      </div>
      <div className="hidden lg:block irp-slide-scene">
        <style>{`
          .irp-slide-scene * { box-sizing: border-box; }
          .irp-slide-scene .scene { width: 100%; max-width: 700px; aspect-ratio: 700 / 460; margin: 0 auto; position: relative; background: linear-gradient(170deg, #eef2ff 0%, #e0e7ff 60%, #ddd6fe 100%); border-radius: 20px; overflow: hidden; font-family: system-ui, -apple-system, sans-serif; box-shadow: 0 20px 50px rgba(0,0,0,0.25); }

          .irp-slide-scene .confetti { position: absolute; top: -20px; animation: irpPsConfettiFall linear infinite; }
          .irp-slide-scene .confetti span { display: block; border-radius: 2px; }
          .irp-slide-scene .c1 { left: 8%; animation-duration: 3.5s; animation-delay: 0.2s; }
          .irp-slide-scene .c1 span { width: 8px; height: 8px; background: #6366f1; animation: irpPsConfettiSpin 1s linear infinite; }
          .irp-slide-scene .c2 { left: 18%; animation-duration: 4s; animation-delay: 0.8s; }
          .irp-slide-scene .c2 span { width: 6px; height: 10px; background: #f59e0b; animation: irpPsConfettiSpin 1.2s linear infinite reverse; }
          .irp-slide-scene .c3 { left: 30%; animation-duration: 3.2s; animation-delay: 0.1s; }
          .irp-slide-scene .c3 span { width: 10px; height: 6px; background: #22c55e; animation: irpPsConfettiSpin 0.8s linear infinite; }
          .irp-slide-scene .c4 { left: 42%; animation-duration: 4.5s; animation-delay: 1.2s; }
          .irp-slide-scene .c4 span { width: 7px; height: 7px; background: #818cf8; border-radius: 50%; }
          .irp-slide-scene .c5 { left: 55%; animation-duration: 3.8s; animation-delay: 0.5s; }
          .irp-slide-scene .c5 span { width: 5px; height: 12px; background: #f59e0b; animation: irpPsConfettiSpin 1.5s linear infinite; }
          .irp-slide-scene .c6 { left: 65%; animation-duration: 3s; animation-delay: 1s; }
          .irp-slide-scene .c6 span { width: 9px; height: 5px; background: #6366f1; animation: irpPsConfettiSpin 0.9s linear infinite reverse; }
          .irp-slide-scene .c7 { left: 78%; animation-duration: 4.2s; animation-delay: 0.3s; }
          .irp-slide-scene .c7 span { width: 6px; height: 6px; background: #22c55e; border-radius: 50%; }
          .irp-slide-scene .c8 { left: 88%; animation-duration: 3.6s; animation-delay: 0.7s; }
          .irp-slide-scene .c8 span { width: 8px; height: 4px; background: #a5b4fc; animation: irpPsConfettiSpin 1.1s linear infinite; }
          .irp-slide-scene .c9 { left: 50%; animation-duration: 3.4s; animation-delay: 1.5s; }
          .irp-slide-scene .c9 span { width: 7px; height: 7px; background: #fbbf24; animation: irpPsConfettiSpin 1.3s linear infinite reverse; }
          .irp-slide-scene .c10 { left: 25%; animation-duration: 4.8s; animation-delay: 0.9s; }
          .irp-slide-scene .c10 span { width: 5px; height: 9px; background: #4f46e5; animation: irpPsConfettiSpin 1s linear infinite; }

          @keyframes irpPsConfettiFall { 0% { top: -20px; opacity: 1; transform: translateX(0); } 25% { transform: translateX(15px); } 50% { transform: translateX(-10px); } 75% { transform: translateX(8px); } 100% { top: 480px; opacity: 0.3; transform: translateX(-5px); } }
          @keyframes irpPsConfettiSpin { 0% { transform: rotateZ(0deg) rotateY(0deg); } 100% { transform: rotateZ(360deg) rotateY(180deg); } }

          .irp-slide-scene .spotlight { position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 300px; height: 400px; background: radial-gradient(ellipse at top, rgba(251,191,36,0.08) 0%, transparent 70%); pointer-events: none; }

          .irp-slide-scene .person { position: absolute; bottom: 100px; left: 50%; transform: translateX(-50%); animation: irpPsPersonBounce 1s ease-in-out infinite; }
          .irp-slide-scene .p-head { width: 58px; height: 58px; border-radius: 50%; background: #C68642; margin: 0 auto; position: relative; }
          .irp-slide-scene .p-hair { position: absolute; top: -2px; left: -3px; width: 64px; height: 32px; background: #1a1a2e; border-radius: 32px 32px 0 0; }
          .irp-slide-scene .p-eye { width: 7px; height: 3px; border-bottom: 2.5px solid #1e1b4b; border-radius: 0 0 50% 50%; position: absolute; top: 30px; }
          .irp-slide-scene .p-eye.l { left: 14px; }
          .irp-slide-scene .p-eye.r { right: 14px; }
          .irp-slide-scene .p-smile { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); width: 22px; height: 12px; border-radius: 0 0 12px 12px; background: white; border: 2px solid #8B6F5E; border-top: none; }
          .irp-slide-scene .p-body { width: 56px; height: 60px; background: #6366f1; border-radius: 14px 14px 6px 6px; margin: -2px auto 0; position: relative; }
          .irp-slide-scene .p-collar { position: absolute; top: 0; left: 50%; transform: translateX(-50%); border-left: 12px solid transparent; border-right: 12px solid transparent; border-top: 16px solid rgba(255,255,255,0.85); }
          .irp-slide-scene .p-arm { position: absolute; width: 12px; height: 50px; border-radius: 6px; top: 5px; }
          .irp-slide-scene .p-arm.l { left: -18px; background: #6366f1; transform-origin: top center; animation: irpPsArmRaise 1s ease-in-out infinite; }
          .irp-slide-scene .p-arm.r { right: -18px; background: #6366f1; transform-origin: top center; animation: irpPsArmRaise 1s ease-in-out infinite 0.15s; }
          .irp-slide-scene .p-hand { width: 14px; height: 14px; border-radius: 50%; background: #C68642; position: absolute; bottom: -4px; left: -1px; }
          .irp-slide-scene .p-legs { display: flex; gap: 6px; justify-content: center; margin-top: -2px; }
          .irp-slide-scene .p-leg { width: 14px; height: 40px; background: #312e81; border-radius: 4px; }

          @keyframes irpPsPersonBounce { 0%, 100% { transform: translateX(-50%) translateY(0); } 40% { transform: translateX(-50%) translateY(-18px); } 60% { transform: translateX(-50%) translateY(-18px); } }
          @keyframes irpPsArmRaise { 0%, 100% { transform: rotate(0deg); } 40% { transform: rotate(-140deg); } 60% { transform: rotate(-140deg); } }

          .irp-slide-scene .starburst { position: absolute; animation: irpPsBurstPop 1.5s ease-out infinite; }
          .irp-slide-scene .starburst svg { width: 24px; height: 24px; }
          .irp-slide-scene .sb1 { top: 100px; left: calc(50% - 120px); animation-delay: 0.3s; }
          .irp-slide-scene .sb2 { top: 95px; left: calc(50% + 100px); animation-delay: 0.8s; }
          .irp-slide-scene .sb3 { top: 140px; left: calc(50% - 85px); animation-delay: 1.2s; }
          .irp-slide-scene .sb4 { top: 135px; left: calc(50% + 70px); animation-delay: 0.6s; }
          @keyframes irpPsBurstPop { 0% { transform: scale(0) rotate(0deg); opacity: 0; } 30% { transform: scale(1.2) rotate(20deg); opacity: 1; } 60% { transform: scale(0.9) rotate(-5deg); opacity: 0.8; } 100% { transform: scale(0) rotate(40deg); opacity: 0; } }

          .irp-slide-scene .card { position: absolute; background: white; border-radius: 14px; box-shadow: 0 6px 24px rgba(0,0,0,0.08); overflow: hidden; }

          .irp-slide-scene .offer-card { left: 35px; top: 140px; width: 165px; height: 140px; opacity: 0; animation: irpPsCardFlyIn 0.8s ease-out 0.5s both, irpPsCardFloat 4s ease-in-out 0.5s infinite; }
          .irp-slide-scene .offer-header { background: #6366f1; padding: 10px 14px; }
          .irp-slide-scene .offer-header span { color: white; font-size: 11px; font-weight: 700; }
          .irp-slide-scene .offer-body { padding: 10px 14px; }
          .irp-slide-scene .offer-line { height: 3px; background: #e0e7ff; border-radius: 2px; margin-bottom: 6px; }
          .irp-slide-scene .offer-line:nth-child(1) { width: 100%; }
          .irp-slide-scene .offer-line:nth-child(2) { width: 85%; }
          .irp-slide-scene .offer-line:nth-child(3) { width: 70%; }
          .irp-slide-scene .offer-badge { display: inline-flex; align-items: center; gap: 4px; background: #dcfce7; color: #16a34a; font-size: 9px; font-weight: 700; padding: 4px 10px; border-radius: 10px; margin-top: 6px; }
          .irp-slide-scene .offer-badge::before { content: '✓'; }

          .irp-slide-scene .score-card { right: 35px; top: 140px; width: 165px; height: 140px; opacity: 0; animation: irpPsCardFlyInR 0.8s ease-out 0.8s both, irpPsCardFloat 4.5s ease-in-out 0.8s infinite; padding: 14px; }
          .irp-slide-scene .score-label { font-size: 10px; color: #1e1b4b; font-weight: 700; }
          .irp-slide-scene .score-ring { width: 70px; height: 70px; margin: 8px auto 0; position: relative; }
          .irp-slide-scene .score-ring svg { width: 70px; height: 70px; transform: rotate(-90deg); }
          .irp-slide-scene .ring-bg { fill: none; stroke: #e0e7ff; stroke-width: 6; }
          .irp-slide-scene .ring-fill { fill: none; stroke: #6366f1; stroke-width: 6; stroke-linecap: round; stroke-dasharray: 188; stroke-dashoffset: 188; animation: irpPsRingDraw 2s ease-out 1.5s forwards; }
          .irp-slide-scene .score-num { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-size: 18px; font-weight: 800; color: #4f46e5; }
          .irp-slide-scene .score-sub { text-align: center; font-size: 8px; color: #22c55e; font-weight: 600; margin-top: 4px; }

          @keyframes irpPsCardFlyIn { 0% { opacity: 0; transform: translateX(-40px) scale(0.9); } 100% { opacity: 1; transform: translateX(0) scale(1); } }
          @keyframes irpPsCardFlyInR { 0% { opacity: 0; transform: translateX(40px) scale(0.9); } 100% { opacity: 1; transform: translateX(0) scale(1); } }
          @keyframes irpPsCardFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          @keyframes irpPsRingDraw { 0% { stroke-dashoffset: 188; } 100% { stroke-dashoffset: 15; } }

          .irp-slide-scene .banner { position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); background: #4f46e5; padding: 12px 36px; border-radius: 14px; box-shadow: 0 4px 20px rgba(79,70,229,0.3); display: flex; flex-direction: column; align-items: center; gap: 2px; opacity: 0; animation: irpPsBannerRise 0.6s ease-out 1.2s both; }
          .irp-slide-scene .banner .b-sub { font-size: 11px; color: #c7d2fe; }
          .irp-slide-scene .banner .b-main { font-size: 16px; font-weight: 800; color: white; letter-spacing: 1px; }
          @keyframes irpPsBannerRise { 0% { opacity: 0; transform: translateX(-50%) translateY(15px); } 100% { opacity: 1; transform: translateX(-50%) translateY(0); } }

          .irp-slide-scene .rounds-badge { position: absolute; top: 20px; left: 50%; transform: translateX(-50%); background: white; border-radius: 20px; padding: 6px 18px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); font-size: 11px; color: #4f46e5; font-weight: 700; opacity: 0; animation: irpPsBadgeDrop 0.5s ease-out 0.3s both; white-space: nowrap; }
          @keyframes irpPsBadgeDrop { 0% { opacity: 0; transform: translateX(-50%) translateY(-10px); } 100% { opacity: 1; transform: translateX(-50%) translateY(0); } }
        `}</style>

        <div className="scene">
          <div className="spotlight"></div>

          <div className="confetti c1"><span></span></div>
          <div className="confetti c2"><span></span></div>
          <div className="confetti c3"><span></span></div>
          <div className="confetti c4"><span></span></div>
          <div className="confetti c5"><span></span></div>
          <div className="confetti c6"><span></span></div>
          <div className="confetti c7"><span></span></div>
          <div className="confetti c8"><span></span></div>
          <div className="confetti c9"><span></span></div>
          <div className="confetti c10"><span></span></div>

          <div className="rounds-badge">✓ 30/30 Rounds Completed</div>

          <div className="starburst sb1"><svg viewBox="0 0 24 24"><polygon points="12,2 15,9 22,9 16,14 18,22 12,17 6,22 8,14 2,9 9,9" fill="#f59e0b" /></svg></div>
          <div className="starburst sb2"><svg viewBox="0 0 24 24"><polygon points="12,2 15,9 22,9 16,14 18,22 12,17 6,22 8,14 2,9 9,9" fill="#f59e0b" /></svg></div>
          <div className="starburst sb3"><svg viewBox="0 0 24 24"><polygon points="12,2 15,9 22,9 16,14 18,22 12,17 6,22 8,14 2,9 9,9" fill="#6366f1" /></svg></div>
          <div className="starburst sb4"><svg viewBox="0 0 24 24"><polygon points="12,2 15,9 22,9 16,14 18,22 12,17 6,22 8,14 2,9 9,9" fill="#22c55e" /></svg></div>

          <div className="person">
            <div className="p-head">
              <div className="p-hair"></div>
              <div className="p-eye l"></div>
              <div className="p-eye r"></div>
              <div className="p-smile"></div>
            </div>
            <div className="p-body">
              <div className="p-collar"></div>
              <div className="p-arm l"><div className="p-hand"></div></div>
              <div className="p-arm r"><div className="p-hand"></div></div>
            </div>
            <div className="p-legs">
              <div className="p-leg"></div>
              <div className="p-leg"></div>
            </div>
          </div>

          <div className="card offer-card">
            <div className="offer-header"><span>Offer Letter</span></div>
            <div className="offer-body">
              <div className="offer-line"></div>
              <div className="offer-line"></div>
              <div className="offer-line"></div>
              <div className="offer-badge">ACCEPTED</div>
            </div>
          </div>

          <div className="card score-card">
            <div className="score-label">Readiness Score</div>
            <div className="score-ring">
              <svg viewBox="0 0 70 70">
                <circle className="ring-bg" cx="35" cy="35" r="30" />
                <circle className="ring-fill" cx="35" cy="35" r="30" />
              </svg>
              <div className="score-num">92%</div>
            </div>
            <div className="score-sub">✓ Interview Ready</div>
          </div>

          <div className="banner">
            <span className="b-sub">Your structured path to</span>
            <span className="b-main">PLACEMENT SUCCESS</span>
          </div>
        </div>
      </div>
    </div>
  );
}
